import React, { useState, useMemo, useEffect, useCallback } from 'react';
import RelatedAlerts from './RelatedAlerts';
import ThreatIntel from './ThreatIntel';
import DeviceContext from './DeviceContext';
import SHAPPanel from './SHAPPanel';
import { apiFetch } from '../../utils/apiClient';
import { useAuth } from '../../AuthGate';

const DISPOSITIONS = [
  { value: 'true_positive',  label: 'True Positive',  color: '#ff6b7a' },
  { value: 'false_positive', label: 'False Positive', color: '#34d399' },
  { value: 'escalated',      label: 'Escalate',       color: '#f59e0b' },
];

const SEV_COLORS = { CRITICAL: '#ff3b4e', HIGH: '#f97316', MEDIUM: '#f59e0b', LOW: '#10b981', NONE: '#374151' };
const PAGE_SIZE = 20;

function getDeviceType(ip) {
  if (!ip) return 'Unknown';
  if (ip.startsWith('10.0.'))  return 'SCADA Node';
  if (ip.startsWith('10.10.')) return 'Traffic Sensor';
  if (ip.startsWith('10.20.')) return 'IP Camera';
  if (ip.startsWith('192.168.')) return 'Smart Meter';
  if (ip.startsWith('172.16.')) return 'IoT Gateway';
  return 'Unknown';
}

const DEVICE_TYPES = ['SCADA Node', 'Traffic Sensor', 'IP Camera', 'Smart Meter', 'IoT Gateway', 'Unknown'];

export default function AlertsTable({ alerts = [] }) {
  const [search,     setSearch]     = useState('');
  const [sev,        setSev]        = useState('');
  const [pred,       setPred]       = useState('');
  const [deviceType, setDeviceType] = useState('');
  const [sortKey,    setSortKey]    = useState('timestamp');
  const [sortDir,    setSortDir]    = useState(-1);
  const [page,       setPage]       = useState(1);
  const [selectedAlert, setSelectedAlert] = useState(null);

  // ── acknowledgement state ──
  const { canDo } = useAuth() || { canDo: () => false };
  const [acks, setAcks] = useState({});          // { alert_id: {acked_by, disposition, ...} }
  const [ackBusy, setAckBusy] = useState(null);   // alert_id currently being acked

  const canAckTier = (tier) =>
    tier === 1 ? canDo('ack_tier1_alert') : canDo('ack_tier2_alert');

  const loadAcks = useCallback(async () => {
    try {
      const res = await apiFetch('/alerts/acks');
      if (res.ok) {
        const data = await res.json();
        setAcks(data.acks || {});
      }
    } catch { /* non-fatal */ }
  }, []);

  useEffect(() => {
    loadAcks();
    const t = setInterval(loadAcks, 10000); // refresh ack state periodically
    return () => clearInterval(t);
  }, [loadAcks]);

  async function acknowledge(alert, disposition) {
    if (!alert?.id) return;
    setAckBusy(alert.id);
    try {
      const res = await apiFetch(`/alerts/${alert.id}/ack`, {
        method: 'POST',
        body: JSON.stringify({ disposition }),
      });
      if (res.ok) {
        const data = await res.json();
        setAcks(prev => ({ ...prev, [alert.id]: {
          alert_id: alert.id, tier: data.tier, acked_by: data.acked_by,
          acked_at: data.acked_at, disposition: data.disposition,
        }}));
      } else if (res.status === 403) {
        // permission/queue mismatch — shouldn't happen if UI gates correctly
        console.warn('Not permitted to ack this tier');
      }
    } catch { /* ignore */ }
    finally { setAckBusy(null); }
  }

  async function unacknowledge(alertId) {
    setAckBusy(alertId);
    try {
      const res = await apiFetch(`/alerts/${alertId}/ack`, { method: 'DELETE' });
      if (res.ok) {
        setAcks(prev => { const n = { ...prev }; delete n[alertId]; return n; });
      }
    } catch { /* ignore */ }
    finally { setAckBusy(null); }
  }

  const filtered = useMemo(() => {
    let r = [...alerts];
    if (sev)  r = r.filter(a => (a.severity || a.threat_level || '').toUpperCase() === sev);
    if (pred) r = r.filter(a => {
      const p = (a.prediction || '').toUpperCase();
      if (pred === 'APT')    return p === 'APT';
      if (pred === 'BENIGN') return p === 'BENIGN' || p === 'NORMAL';
      return true;
    });
    if (deviceType) {
      r = r.filter(a => getDeviceType(a.src_ip || a.source_ip || '') === deviceType);
    }
    if (search) {
      const s = search.toLowerCase();
      r = r.filter(a =>
        (a.src_ip || a.source_ip || '').includes(s) ||
        (a.attack_type || '').toLowerCase().includes(s)
      );
    }
    r.sort((a, b) => {
      let av = a[sortKey], bv = b[sortKey];
      if (typeof av === 'number') return sortDir * (av - bv);
      return sortDir * String(av || '').localeCompare(String(bv || ''));
    });
    return r;
  }, [alerts, sev, pred, deviceType, search, sortKey, sortDir]);

  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const sort  = (k) => { setSortKey(k); setSortDir(k === sortKey ? -sortDir : -1); setPage(1); };

  // Device type counts for badge
  const deviceCounts = useMemo(() => {
    const counts = {};
    alerts.forEach(a => {
      const dt = getDeviceType(a.src_ip || a.source_ip || '');
      counts[dt] = (counts[dt] || 0) + 1;
    });
    return counts;
  }, [alerts]);

  const selectStyle = {
    background: '#141720', border: '1px solid #ffffff22', borderRadius: 5,
    color: '#9ca3af', padding: '5px 10px', fontSize: 12,
    fontFamily: 'JetBrains Mono, monospace', outline: 'none',
  };

  const thStyle = {
    padding: '8px 12px', textAlign: 'left', fontSize: 10, color: '#6b7280',
    fontWeight: 500, letterSpacing: '.08em', borderBottom: '1px solid #ffffff14',
    background: '#1a1e2a', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap',
  };

  const DEVICE_ICONS = {
    'SCADA Node':     '⚡',
    'Traffic Sensor': '🚦',
    'IP Camera':      '📷',
    'Smart Meter':    '📊',
    'IoT Gateway':    '🔗',
    'Unknown':        '❓',
  };

  return (
    <div style={{ background: '#141720', border: '1px solid #ffffff14', borderRadius: 8, overflow: 'hidden' }}>
      {/* Toolbar */}
      <div style={{ padding: '10px 14px', borderBottom: '1px solid #ffffff14', display: 'flex', alignItems: 'center', gap: 10, background: '#1a1e2a', flexWrap: 'wrap' }}>
        <input placeholder="Search IP / type..." value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
          style={{ ...selectStyle, width: 180 }} />

        <select value={sev} onChange={e => { setSev(e.target.value); setPage(1); }} style={selectStyle}>
          <option value="">All Severities</option>
          <option value="CRITICAL">CRITICAL</option>
          <option value="HIGH">HIGH</option>
          <option value="MEDIUM">MEDIUM</option>
          <option value="LOW">LOW</option>
        </select>

        <select value={pred} onChange={e => { setPred(e.target.value); setPage(1); }} style={selectStyle}>
          <option value="">All Predictions</option>
          <option value="APT">Attack</option>
          <option value="BENIGN">Normal</option>
        </select>

        {/* Device Type filter */}
        <select value={deviceType} onChange={e => { setDeviceType(e.target.value); setPage(1); }} style={{ ...selectStyle, minWidth: 160 }}>
          <option value="">All Device Types</option>
          {DEVICE_TYPES.map(dt => (
            <option key={dt} value={dt}>
              {DEVICE_ICONS[dt]} {dt} {deviceCounts[dt] ? `(${deviceCounts[dt].toLocaleString()})` : ''}
            </option>
          ))}
        </select>

        <span style={{ flex: 1 }} />

        {/* Active filter badges */}
        {deviceType && (
          <span style={{ fontSize: 10, fontFamily: 'JetBrains Mono, monospace', color: '#f59e0b', background: '#f59e0b18', border: '1px solid #f59e0b33', borderRadius: 4, padding: '2px 8px', display: 'flex', alignItems: 'center', gap: 4 }}>
            {DEVICE_ICONS[deviceType]} {deviceType}
            <span onClick={() => setDeviceType('')} style={{ cursor: 'pointer', marginLeft: 4, color: '#6b7280' }}>✕</span>
          </span>
        )}

        <span style={{ fontSize: 11, fontFamily: 'JetBrains Mono, monospace', color: '#6b7280', background: '#141720', border: '1px solid #ffffff14', borderRadius: 4, padding: '2px 8px' }}>
          {filtered.length.toLocaleString()} alerts
        </span>
      </div>

      {/* Table */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'JetBrains Mono, monospace', fontSize: 12 }}>
          <thead>
            <tr>
              {[
                ['timestamp',   'TIMESTAMP'],
                ['src_ip',      'SOURCE IP'],
                ['attack_type', 'ATTACK TYPE'],
                ['prediction',  'PREDICTION'],
                ['confidence',  'CONFIDENCE'],
                ['severity',    'SEVERITY'],
              ].map(([k, l]) => (
                <th key={k} onClick={() => sort(k)}
                  style={{ padding: '8px 12px', textAlign: 'left', fontSize: 10, color: '#6b7280', fontWeight: 500, letterSpacing: '.08em', borderBottom: '1px solid #ffffff14', background: '#1a1e2a', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>
                  {l} {sortKey === k ? (sortDir === -1 ? '↓' : '↑') : '↕'}
                </th>
              ))}
              <th style={thStyle}>DEVICE TYPE</th>
              <th style={thStyle}>THREAT INTEL</th>
              <th style={thStyle}>ACKNOWLEDGE</th>
            </tr>
          </thead>
          <tbody>
            {paged.length === 0 ? (
              <tr><td colSpan={9} style={{ padding: 32, textAlign: 'center', color: '#374151' }}>No alerts match filter</td></tr>
            ) : paged.map((a, i) => {
              const conf     = Math.round((a.confidence || 0) * 100);
              const col      = conf > 85 ? '#ff3b4e' : conf > 60 ? '#f59e0b' : '#10b981';
              const sevKey   = (a.severity || a.threat_level || 'LOW').toUpperCase();
              const sevColor = SEV_COLORS[sevKey] || '#6b7280';
              const srcIp    = a.src_ip || a.source_ip || '—';
              const isAPT    = (a.prediction || '').toUpperCase() === 'APT';
              const isSelected = selectedAlert === a;

              return (
                <tr key={i}
                  style={{ borderBottom: '1px solid #ffffff0a', cursor: 'pointer', background: isSelected ? '#1a1e2a' : 'transparent', borderLeft: isSelected ? '3px solid #3b82f6' : '3px solid transparent' }}
                  onClick={() => setSelectedAlert(isSelected ? null : a)}
                  onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = '#1a1e2a66'; }}
                  onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}>
                  <td style={{ padding: '7px 12px', color: '#6b7280' }}>{a.timestamp}</td>
                  <td style={{ padding: '7px 12px', color: '#06b6d4' }}>{srcIp}</td>
                  <td style={{ padding: '7px 12px', color: '#e8eaf0' }}>{a.attack_type || '—'}</td>
                  <td style={{ padding: '7px 12px', color: isAPT ? '#ff6b7a' : '#34d399', fontWeight: 600 }}>{a.prediction}</td>
                  <td style={{ padding: '7px 12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ flex: 1, height: 4, background: '#ffffff14', borderRadius: 2, overflow: 'hidden', minWidth: 60 }}>
                        <div style={{ width: `${conf}%`, height: '100%', background: col, borderRadius: 2 }} />
                      </div>
                      <span style={{ fontSize: 10, color: '#6b7280', minWidth: 30 }}>{conf}%</span>
                    </div>
                  </td>
                  <td style={{ padding: '7px 12px' }}>
                    {sevKey !== 'NONE' && (
                      <span style={{ padding: '2px 7px', borderRadius: 3, fontSize: 10, fontWeight: 500, background: `${sevColor}18`, color: sevColor, border: `1px solid ${sevColor}33` }}>
                        {sevKey}
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '7px 12px' }}>
                    <DeviceContext ip={srcIp} />
                  </td>
                  <td style={{ padding: '7px 12px' }}>
                    <ThreatIntel ip={srcIp} />
                  </td>
                  <td style={{ padding: '7px 12px', whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                    <AckCell
                      alert={a}
                      ack={acks[a.id]}
                      busy={ackBusy === a.id}
                      canAck={canAckTier(a.tier || 1)}
                      onAck={acknowledge}
                      onUnack={unacknowledge}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <RelatedAlerts alert={selectedAlert} allAlerts={alerts} onClose={() => setSelectedAlert(null)} />

      {selectedAlert && (
        <div style={{ padding: '0 14px 14px 14px' }}>
          <SHAPPanel
            attackType={selectedAlert.attack_type}
            confidence={selectedAlert.confidence}
            prediction={selectedAlert.prediction}
          />
        </div>
      )}

      {/* Footer */}
      <div style={{ padding: '8px 14px', borderTop: '1px solid #ffffff14', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 11, color: '#6b7280', fontFamily: 'JetBrains Mono, monospace' }}>
          Page {page} of {pages} · {filtered.length.toLocaleString()} total
        </span>
        <div style={{ display: 'flex', gap: 6 }}>
          <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page === 1}
            style={{ padding: '4px 10px', borderRadius: 4, fontSize: 11, cursor: 'pointer', border: '1px solid #ffffff22', background: 'transparent', color: '#9ca3af', fontFamily: 'JetBrains Mono, monospace', opacity: page === 1 ? .4 : 1 }}>← Prev</button>
          <span style={{ fontSize: 11, color: '#9ca3af', fontFamily: 'JetBrains Mono, monospace', padding: '4px 8px' }}>{page}/{pages}</span>
          <button onClick={() => setPage(p => Math.min(pages, p+1))} disabled={page === pages}
            style={{ padding: '4px 10px', borderRadius: 4, fontSize: 11, cursor: 'pointer', border: '1px solid #ffffff22', background: 'transparent', color: '#9ca3af', fontFamily: 'JetBrains Mono, monospace', opacity: page === pages ? .4 : 1 }}>Next →</button>
        </div>
      </div>
    </div>
  );
}

// ── AckCell: per-row acknowledgement control ──────────────────────────────────
// Shows the ack state if already acknowledged, otherwise (if the user has the
// right permission for this alert's tier) a disposition dropdown + Ack button.
// Guests, and analysts of the other tier, see a read-only dash.
function AckCell({ alert, ack, busy, canAck, onAck, onUnack }) {
  const [disp, setDisp] = useState('true_positive');
  const mono = { fontFamily: 'JetBrains Mono, monospace' };

  if (ack) {
    const d = DISPOSITIONS.find(x => x.value === ack.disposition);
    const col = d?.color || '#9ca3af';
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ fontSize: 10, ...mono, color: col, background: `${col}18`, border: `1px solid ${col}33`, borderRadius: 3, padding: '2px 7px' }}>
          ✓ {d?.label || ack.disposition}
        </span>
        <span style={{ fontSize: 9, color: '#6b7280', ...mono }} title={ack.acked_at}>
          by {ack.acked_by}
        </span>
        {canAck && (
          <button onClick={() => onUnack(alert.id)} disabled={busy}
            style={{ fontSize: 9, ...mono, color: '#6b7280', background: 'transparent', border: '1px solid #ffffff14', borderRadius: 3, padding: '1px 5px', cursor: 'pointer', opacity: busy ? 0.5 : 1 }}
            title="Remove acknowledgement">✕</button>
        )}
      </div>
    );
  }

  if (!canAck) {
    return <span style={{ fontSize: 10, color: '#374151', ...mono }}>—</span>;
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
      <select value={disp} onChange={e => setDisp(e.target.value)}
        style={{ background: '#0d1018', border: '1px solid #ffffff22', borderRadius: 4, color: '#9ca3af', padding: '3px 6px', fontSize: 10, ...mono, outline: 'none' }}>
        {DISPOSITIONS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
      </select>
      <button onClick={() => onAck(alert, disp)} disabled={busy}
        style={{ fontSize: 10, ...mono, color: '#60a5fa', background: '#3b82f622', border: '1px solid #3b82f644', borderRadius: 4, padding: '3px 9px', cursor: 'pointer', opacity: busy ? 0.5 : 1 }}>
        {busy ? '⟳' : 'Ack'}
      </button>
    </div>
  );
}